/**
 * Articulate (a8e) CLI Executor
 * High-priority local sub-agent for coding and local processing tasks.
 * Integrates with the Articulate CLI for AI-assisted development.
 * https://github.com/a8e-ai/a8e
 */

import { spawn, type ChildProcess } from 'child_process';
import { promisify } from 'util';
import { exec as execCallback } from 'child_process';
import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'fs';
import { join } from 'path';
import { homedir } from 'os';
import type { ExecutorOptions, ExecutorResult, AvailabilityStatus } from '../types.js';
import type { AgentExecutor } from './index.js';

const exec = promisify(execCallback);

const A8E_CONFIG_DIR = join(homedir(), '.config', 'a8e');
const A8E_CONFIG_PATH = join(A8E_CONFIG_DIR, 'config.yaml');
const A8E_SECRETS_PATH = join(A8E_CONFIG_DIR, 'secrets.yaml');

const A8E_DEFAULT_PROVIDER = 'paean_ai';
const A8E_DEFAULT_MODEL = 'gemini-3.1-flash-preview';

export interface A8eConfig {
    provider?: string;
    model?: string;
    hasApiKey?: boolean;
}

export class ArticulateExecutor implements AgentExecutor {
    readonly type = 'articulate' as const;
    private process: ChildProcess | null = null;
    private binaryPath: string;

    constructor(binaryPath: string = 'a8e') {
        this.binaryPath = binaryPath;
    }

    async isAvailable(): Promise<boolean> {
        try {
            await exec(`which ${this.binaryPath}`);
            return true;
        } catch {
            return false;
        }
    }

    async checkAvailability(): Promise<AvailabilityStatus> {
        try {
            const { stdout: whichOut } = await exec(`which ${this.binaryPath}`);
            const binaryPath = whichOut.trim();

            try {
                const { stdout } = await exec(`${this.binaryPath} info`, { timeout: 10000 });
                const versionMatch = stdout.match(/(?:v(?:ersion)?\s*)?(\d+\.\d+(?:\.\d+)?)/i);

                return {
                    available: true,
                    binaryExists: true,
                    binaryPath,
                    authStatus: 'authenticated',
                    version: versionMatch?.[1],
                };
            } catch {
                return {
                    available: true,
                    binaryExists: true,
                    binaryPath,
                    authStatus: 'unknown',
                    authMessage: 'a8e uses BYOK — ensure a provider is configured via `paean a8e configure`',
                };
            }
        } catch {
            return {
                available: false,
                binaryExists: false,
                authStatus: 'unknown',
                error: `Binary '${this.binaryPath}' not found. Install with: paean a8e install`,
            };
        }
    }

    async execute(prompt: string, options?: ExecutorOptions): Promise<ExecutorResult> {
        const startTime = Date.now();
        const args = this.buildArgs(prompt, options);

        return new Promise((resolve) => {
            let stdout = '';
            let stderr = '';
            const timeout = options?.timeout ?? 1800000;

            const a8eEnv = this.buildA8eEnv(options?.env);

            this.process = spawn(this.binaryPath, args, {
                cwd: options?.cwd,
                env: a8eEnv,
                stdio: ['ignore', 'pipe', 'pipe'],
            });

            const timeoutId = setTimeout(() => {
                if (this.process) {
                    this.process.kill('SIGTERM');
                    resolve({
                        success: false,
                        output: stdout,
                        error: `Execution timed out after ${timeout}ms`,
                        durationMs: Date.now() - startTime,
                    });
                }
            }, timeout);

            this.process.stdout?.on('data', (data) => {
                const text = data.toString();
                stdout += text;
                options?.onOutput?.(text, 'stdout');
            });

            this.process.stderr?.on('data', (data) => {
                const text = data.toString();
                stderr += text;
                options?.onOutput?.(text, 'stderr');
            });

            this.process.on('close', (code) => {
                clearTimeout(timeoutId);
                this.process = null;

                let structured: Record<string, unknown> | undefined;
                try {
                    if (stdout.trim().startsWith('{')) {
                        structured = JSON.parse(stdout);
                    }
                } catch {
                    // Not JSON
                }

                resolve({
                    success: code === 0,
                    output: stdout,
                    error: code !== 0 ? stderr || `Exit code: ${code}` : undefined,
                    exitCode: code ?? undefined,
                    durationMs: Date.now() - startTime,
                    structured,
                });
            });

            this.process.on('error', (err) => {
                clearTimeout(timeoutId);
                this.process = null;

                resolve({
                    success: false,
                    output: stdout,
                    error: err.message,
                    durationMs: Date.now() - startTime,
                });
            });
        });
    }

    /**
     * Build a8e CLI arguments for headless execution.
     * Uses `a8e run --text <prompt>` for non-interactive mode.
     */
    private buildArgs(prompt: string, options?: ExecutorOptions): string[] {
        const args: string[] = ['run'];

        args.push('--text', prompt);

        if (options?.captureOutput) {
            args.push('--output-format', 'json');
        }

        if (options?.skipPermissions) {
            args.push('--dangerously-skip-permissions');
        }

        if (options?.args) {
            args.push(...options.args);
        }

        return args;
    }

    /**
     * Build environment variables for the a8e subprocess.
     * Injects Paean AI provider config as env vars so a8e picks them up
     * even if config.yaml hasn't been written yet.
     */
    private buildA8eEnv(extraEnv?: Record<string, string>): NodeJS.ProcessEnv {
        const env: NodeJS.ProcessEnv = { ...process.env };

        const config = this.readA8eConfig();

        if (!env.A8E_PROVIDER && config.provider) {
            env.A8E_PROVIDER = config.provider;
        }
        if (!env.A8E_MODEL && config.model) {
            env.A8E_MODEL = config.model;
        }

        if (extraEnv) {
            Object.assign(env, extraEnv);
        }

        return env;
    }

    /**
     * Read a8e's current configuration from config.yaml + secrets.yaml
     */
    readA8eConfig(): A8eConfig {
        const result: A8eConfig = {};

        try {
            if (existsSync(A8E_CONFIG_PATH)) {
                const content = readFileSync(A8E_CONFIG_PATH, 'utf-8');
                const providerMatch = content.match(/^A8E_PROVIDER:\s*(.+)$/m);
                const modelMatch = content.match(/^A8E_MODEL:\s*(.+)$/m);
                if (providerMatch) result.provider = providerMatch[1].trim();
                if (modelMatch) result.model = modelMatch[1].trim();
            }
        } catch {
            // Config not readable
        }

        try {
            if (existsSync(A8E_SECRETS_PATH)) {
                const content = readFileSync(A8E_SECRETS_PATH, 'utf-8');
                result.hasApiKey = content.includes('PAEAN_AI_API_KEY');
            }
        } catch {
            // Secrets not readable
        }

        return result;
    }

    /**
     * Configure a8e to use Paean AI as provider with the given API key.
     * Writes to ~/.config/a8e/config.yaml and secrets.yaml.
     */
    configureForPaean(apiKey: string, model?: string): void {
        mkdirSync(A8E_CONFIG_DIR, { recursive: true });

        const targetModel = model || A8E_DEFAULT_MODEL;

        let existingConfig = '';
        try {
            if (existsSync(A8E_CONFIG_PATH)) {
                existingConfig = readFileSync(A8E_CONFIG_PATH, 'utf-8');
            }
        } catch {
            // Fresh config
        }

        // Update or insert provider and model keys
        existingConfig = this.upsertYamlKey(existingConfig, 'A8E_PROVIDER', A8E_DEFAULT_PROVIDER);
        existingConfig = this.upsertYamlKey(existingConfig, 'A8E_MODEL', targetModel);

        writeFileSync(A8E_CONFIG_PATH, existingConfig, 'utf-8');

        // Write the API key to secrets.yaml
        let existingSecrets = '';
        try {
            if (existsSync(A8E_SECRETS_PATH)) {
                existingSecrets = readFileSync(A8E_SECRETS_PATH, 'utf-8');
            }
        } catch {
            // Fresh secrets
        }

        existingSecrets = this.upsertYamlKey(existingSecrets, 'PAEAN_AI_API_KEY', apiKey);
        writeFileSync(A8E_SECRETS_PATH, existingSecrets, 'utf-8');
    }

    /**
     * Upsert a key in a simple YAML document (key: value per line).
     */
    private upsertYamlKey(content: string, key: string, value: string): string {
        const regex = new RegExp(`^${key}:.*$`, 'm');
        const line = `${key}: ${value}`;

        if (regex.test(content)) {
            return content.replace(regex, line);
        }

        if (content.length > 0 && !content.endsWith('\n')) {
            content += '\n';
        }
        return content + line + '\n';
    }

    abort(): void {
        if (this.process) {
            this.process.kill('SIGTERM');
            this.process = null;
        }
    }
}

/** Static helpers for the a8e config directory */
export const A8E_PATHS = {
    configDir: A8E_CONFIG_DIR,
    configFile: A8E_CONFIG_PATH,
    secretsFile: A8E_SECRETS_PATH,
    defaultProvider: A8E_DEFAULT_PROVIDER,
    defaultModel: A8E_DEFAULT_MODEL,
};
